import { setup } from 'xstate';

/**
 * F-016 `sourceRelevanceIndexMachine` (§XI — data-model §2.2). The TS use
 * case consumes this machine to decide whether firing an async `ensure`
 * build is legal for the observed per-trial manifest state; the Python
 * section-search Lambda is the executor only (it writes manifest states, it
 * never models transitions — and Step Functions is not involved, so nothing
 * is double-modeled).
 *
 *   absent  --ENSURE-->          building
 *   failed  --ENSURE-->          building   (retry)
 *   ready   --ENSURE[force]-->   building   (rebuild after re-upload)
 *   building --BUILD_SUCCEEDED--> ready
 *   building --BUILD_FAILED-->    failed
 */
export type SourceIndexState = 'absent' | 'building' | 'ready' | 'failed';

export type SourceIndexEvent =
  | { readonly type: 'ENSURE'; readonly force?: boolean }
  | { readonly type: 'BUILD_SUCCEEDED' }
  | { readonly type: 'BUILD_FAILED' };

export const sourceRelevanceIndexMachine = setup({
  types: {} as {
    events: SourceIndexEvent;
  },
  guards: {
    forced: ({ event }) => event.type === 'ENSURE' && event.force === true,
  },
}).createMachine({
  id: 'sourceRelevanceIndex',
  initial: 'absent',
  states: {
    absent: {
      on: { ENSURE: { target: 'building' } },
    },
    building: {
      on: {
        BUILD_SUCCEEDED: { target: 'ready' },
        BUILD_FAILED: { target: 'failed' },
      },
    },
    ready: {
      // Rebuild only on an explicit force (e.g. after a re-upload) — a plain
      // ensure against a ready index is a no-op (I-CC-06 keeps queries on
      // ready artefacts; concurrent ensure collapse happens at the manifest
      // conditional write).
      on: { ENSURE: { target: 'building', guard: 'forced' } },
    },
    failed: {
      on: { ENSURE: { target: 'building' } },
    },
  },
});

/**
 * The one question the API's use cases ask: may I fire `ensure` now? Pure
 * projection over the machine's transition table so callers never hand-roll
 * lifecycle logic (§XI: no scattered apply* functions).
 */
export const ensureIsLegal = (state: SourceIndexState, force: boolean): boolean => {
  if (state === 'building') return false;
  if (state === 'ready') return force;
  return true;
};
